;(() => {
  const SELECTOR = 'subscription-selector'

  const EXCLUSION_ELIGIBLE_TRACKS = ['savory', 'mashup']
  const EXCLUSION_ELIGIBLE_DURATION = '12 Months'

  /**
   * Discount percentages by subscription duration (excluding shipping).
   * The Shopify selling_plan price already reflects the discount, so we
   * reverse-calculate the original (non-discounted) price for display.
   */
  const DISCOUNT_BY_DURATION = {
    '3 Months': 10,
    '6 Months': 15,
    '12 Months': 20,
  }

  function safeJsonParse(text) {
    try {
      return JSON.parse(text)
    } catch (error) {
      console.error('Failed to parse subscription products JSON:', error)
      return null
    }
  }

  /**
   * Parse variant title like "3 Months - Product A - Product B"
   * Returns { duration: "3 Months", products: "Product A - Product B" }
   */
  function parseVariantTitle(title) {
    const match = title.match(/^(\d+\s*Months?)\s*-\s*(.+)$/i)
    if (match) {
      return {
        duration: match[1].trim(),
        products: match[2].trim(),
      }
    }
    return {
      duration: null,
      products: title,
    }
  }

  /**
   * Group variants by duration
   * Returns { "3 Months": [{ variant, products }], "6 Months": [...], ... }
   */
  function groupVariantsByDuration(variants) {
    const groups = {}

    variants.forEach((variant, index) => {
      const parsed = parseVariantTitle(variant.title)
      const duration = parsed.duration || 'Default'

      if (!groups[duration]) {
        groups[duration] = []
      }

      groups[duration].push({
        variant,
        index,
        products: parsed.products,
      })
    })

    return groups
  }

  /**
   * Sort durations in order: 3 Months, 6 Months, 12 Months
   */
  function sortDurations(durations) {
    const order = { '3 Months': 1, '6 Months': 2, '12 Months': 3 }
    return durations.sort((a, b) => {
      const orderA = order[a] || 999
      const orderB = order[b] || 999
      return orderA - orderB
    })
  }

  class SubscriptionSelector extends HTMLElement {
    constructor() {
      super()
      this.sectionId = this.dataset.sectionId
      this.products = null
      this.currentProductKey = 'sweet'
      this.currentDuration = null
      this.currentVariantIndex = 0
      this.variantGroups = {}
      this.excludedFlavors = []
      this._submittingFromModal = false
    }

    connectedCallback() {
      this.init()
    }

    init() {
      const dataEl = document.getElementById(`SubscriptionProducts-${this.sectionId}`)
      if (!dataEl) {
        console.error('Subscription products data not found')
        return
      }

      this.products = safeJsonParse(dataEl.textContent)
      if (!this.products) return

      this.cacheElements()
      this.bindEvents()
      this.processCurrentProduct()
      this.renderDurations()
      this.renderProductOptions()
      this.updateFormInputs()
      this.updateMedia()
    }

    cacheElements() {
      this.entryProductInputs = this.querySelectorAll('[data-entry-product]')
      this.durationContainer = this.querySelector('[data-duration-options]')
      this.durationFieldset = this.querySelector('[data-tier="2"]')
      this.productOptionsContainer = this.querySelector('[data-product-options]')
      this.productFieldset = this.querySelector('[data-tier="3"]')
      this.priceDisplay = this.querySelector('[data-subscription-price]')
      this.comparePriceDisplay = this.querySelector('[data-subscription-compare-price]')
      this.discountBadge = this.querySelector('[data-subscription-discount]')
      this.variantIdInput = this.querySelector('[data-variant-id]')
      this.sellingPlanInput = this.querySelector('[data-selling-plan]')
      this.addToCartButton = this.querySelector('[data-add-to-cart]')
      this.addToCartText = this.querySelector('[data-add-to-cart-text]')
      this.form = this.querySelector('[data-subscription-form]')
      this.section = document.querySelector(`#product[data-section="${this.sectionId}"]`)
      this.mediaItems = this.section ? this.section.querySelectorAll('.subscription-media-item') : []

      // Flavor exclusion modal elements
      this.exclusionModal = this.querySelector('[data-flavor-exclusion-modal]')
      this.exclusionOverlay = this.querySelector('[data-flavor-exclusion-overlay]')
      this.exclusionCloseButtons = this.querySelectorAll('[data-flavor-exclusion-close]')
      this.exclusionConfirmButton = this.querySelector('[data-flavor-exclusion-confirm]')
      this.exclusionSkipButton = this.querySelector('[data-flavor-exclusion-skip]')
      this.exclusionCheckboxes = this.querySelectorAll('[data-flavor-exclude]')
      this.sweetExclusionSection = this.querySelector('[data-sweet-exclusion-section]')
      this.savoryExclusionSubtitle = this.querySelector('[data-savory-exclusion-subtitle]')
      this.exclusionSummary = this.querySelector('[data-flavor-exclusion-summary]')
      this.exclusionSummaryText = this.querySelector('[data-flavor-exclusion-summary-text]')
      this.excludedFlavorsInput = this.querySelector('[data-excluded-flavors]')
      this.firstShipProductInput = this.querySelector('[data-first-ship-product]')
    }

    bindEvents() {
      this.entryProductInputs.forEach((input) => {
        input.addEventListener('change', this.onProductChange.bind(this))
      })

      // Intercept form submit for exclusion-eligible configs
      if (this.form) {
        this.form.addEventListener('submit', this.handleFormSubmit.bind(this))
      }

      // Flavor exclusion modal events
      if (this.exclusionOverlay) {
        this.exclusionOverlay.addEventListener('click', (e) => {
          if (e.target === this.exclusionOverlay) this.hideExclusionModal()
        })
      }
      this.exclusionCloseButtons.forEach((btn) => {
        btn.addEventListener('click', this.hideExclusionModal.bind(this))
      })
      if (this.exclusionConfirmButton) {
        this.exclusionConfirmButton.addEventListener('click', this.confirmExclusions.bind(this))
      }
      if (this.exclusionSkipButton) {
        this.exclusionSkipButton.addEventListener('click', this.skipExclusions.bind(this))
      }
      this.exclusionCheckboxes.forEach((cb) => {
        cb.addEventListener('change', this.updateExclusionSummary.bind(this))
      })
      document.addEventListener('keyup', (e) => {
        if (e.key === 'Escape' && this.exclusionModal && !this.exclusionModal.hidden) {
          this.hideExclusionModal()
        }
      })
    }

    onProductChange(event) {
      this.currentProductKey = event.target.value
      this.processCurrentProduct()
      this.renderDurations()
      this.renderProductOptions()
      this.updateFormInputs()
      this.updateMedia()
      this.clearExclusions()
    }

    onDurationChange(event) {
      this.currentDuration = event.target.value
      this.renderProductOptions()
      this.updateFormInputs()
      this.updateMedia()
      this.clearExclusions()
    }

    onProductOptionChange(event) {
      this.currentVariantIndex = parseInt(event.target.value, 10)
      this.updateFormInputs()
    }

    processCurrentProduct() {
      const product = this.products[this.currentProductKey]
      if (!product || !product.variants) {
        this.variantGroups = {}
        this.currentDuration = null
        return
      }

      this.variantGroups = groupVariantsByDuration(product.variants)
      const durations = sortDurations(Object.keys(this.variantGroups))
      this.currentDuration = (this.currentDuration && durations.includes(this.currentDuration))
        ? this.currentDuration
        : durations[0] || null

      if (this.currentDuration && this.variantGroups[this.currentDuration]) {
        this.currentVariantIndex = this.variantGroups[this.currentDuration][0].index
      }
    }

    renderDurations() {
      const durations = sortDurations(Object.keys(this.variantGroups))

      if (durations.length <= 1 && durations[0] === 'Default') {
        this.durationFieldset.style.display = 'none'
        return
      }

      this.durationFieldset.style.display = ''

      const html = durations
        .map((duration, idx) => {
          const inputId = `SubscriptionDuration-${this.sectionId}-${this.currentProductKey}-${idx}`
          const checked = duration === this.currentDuration ? 'checked' : ''
          const variants = this.variantGroups[duration]
          const hasAvailable = variants.some((v) => v.variant.available)
          const disabled = !hasAvailable ? 'disabled' : ''
          const labelStyle = !hasAvailable ? 'style="opacity: 0.5;"' : ''

          return `
            <input
              type="radio"
              id="${inputId}"
              name="subscription_duration_${this.sectionId}"
              value="${duration}"
              ${checked}
              ${disabled}
              data-duration-option
            >
            <label for="${inputId}" ${labelStyle}>
              ${duration}${!hasAvailable ? ' (Sold out)' : ''}
            </label>
          `
        })
        .join('')

      this.durationContainer.innerHTML = html

      const durationInputs = this.durationContainer.querySelectorAll('[data-duration-option]')
      durationInputs.forEach((input) => {
        input.addEventListener('change', this.onDurationChange.bind(this))
      })
    }

    renderProductOptions() {
      if (!this.currentDuration || !this.variantGroups[this.currentDuration]) {
        this.productFieldset.style.display = 'none'
        return
      }

      const variants = this.variantGroups[this.currentDuration]

      this.productFieldset.style.display = ''

      const html = variants
        .map((item, idx) => {
          const inputId = `SubscriptionProduct-${this.sectionId}-${this.currentProductKey}-${this.currentDuration}-${idx}`
          const checked = idx === 0 ? 'checked' : ''
          const disabled = !item.variant.available ? 'disabled' : ''
          const labelStyle = !item.variant.available ? 'style="opacity: 0.5;"' : ''

          return `
            <input
              type="radio"
              id="${inputId}"
              name="subscription_product_option_${this.sectionId}"
              value="${item.index}"
              ${checked}
              ${disabled}
              data-product-option
            >
            <label for="${inputId}" ${labelStyle}>
              ${(() => {
                const names = item.products.split(' - ')
                if (names.length === 1) return `<span class="product-name">${names[0]}</span>`
                return `<span class="product-names-wrap" data-month-count="${names.length}">${names.map((name, i) => `<span class="product-name"><strong>${i + 1}.</strong> ${name}</span>`).join('')}</span>`
              })()}${!item.variant.available ? ' (Sold out)' : ''}
            </label>
          `
        })
        .join('')

      this.productOptionsContainer.innerHTML = html

      const productInputs = this.productOptionsContainer.querySelectorAll('[data-product-option]')
      productInputs.forEach((input) => {
        input.addEventListener('change', this.onProductOptionChange.bind(this))
      })

      // Set current variant index to the first available in this duration
      const firstAvailable = variants.find((v) => v.variant.available)
      this.currentVariantIndex = firstAvailable ? firstAvailable.index : variants[0].index
    }

    updateFormInputs() {
      const product = this.products[this.currentProductKey]
      if (!product || !product.variants || product.variants.length === 0) {
        this.disableForm()
        return
      }

      const variant = product.variants[this.currentVariantIndex]
      if (!variant) {
        this.disableForm()
        return
      }

      this.variantIdInput.value = variant.id

      if (variant.selling_plan_allocations && variant.selling_plan_allocations.length > 0) {
        // Find the allocation that matches the currently selected duration
        // Selling plan names start with the month number (e.g. "3 Months", "6 Months", "12 Months")
        let allocation = variant.selling_plan_allocations[0]
        if (this.currentDuration) {
          const durationMonths = parseInt(this.currentDuration, 10)
          const matched = variant.selling_plan_allocations.find((a) => {
            if (!a.selling_plan_name) return false
            const planMonths = parseInt(a.selling_plan_name, 10)
            return planMonths === durationMonths
          })
          if (matched) {
            allocation = matched
          }
        }
        this.sellingPlanInput.value = allocation.selling_plan_id
        this.updatePrice(allocation.price_formatted, allocation.price)
      } else {
        this.sellingPlanInput.value = ''
        this.updatePrice(variant.price_formatted, variant.price)
      }

      // Set first ship product line item property
      if (this.firstShipProductInput) {
        if (variant.first_ship_product) {
          this.firstShipProductInput.name = 'properties[First Ship Product]'
          this.firstShipProductInput.value = variant.first_ship_product
        } else {
          this.firstShipProductInput.removeAttribute('name')
          this.firstShipProductInput.value = ''
        }
      }

      if (variant.available) {
        this.enableForm()
      } else {
        this.disableForm('Sold out')
      }
    }

    updatePrice(priceFormatted, priceCents) {
      if (this.priceDisplay) {
        this.priceDisplay.textContent = priceFormatted
      }

      const discountPercent = DISCOUNT_BY_DURATION[this.currentDuration]

      if (discountPercent && priceCents != null) {
        // The current price is already discounted. Reverse-calculate the original.
        // originalPrice = discountedPrice / (1 - discount/100)
        const originalCents = Math.round(priceCents / (1 - discountPercent / 100))
        const originalFormatted = this.formatMoneyWhole(originalCents)

        if (this.comparePriceDisplay) {
          this.comparePriceDisplay.textContent = originalFormatted
          this.comparePriceDisplay.hidden = false
        }
        if (this.discountBadge) {
          this.discountBadge.textContent = `Save ${discountPercent}%`
          this.discountBadge.hidden = false
        }
      } else {
        if (this.comparePriceDisplay) {
          this.comparePriceDisplay.textContent = ''
          this.comparePriceDisplay.hidden = true
        }
        if (this.discountBadge) {
          this.discountBadge.textContent = ''
          this.discountBadge.hidden = true
        }
      }
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2)
      return `$${amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
    }

    formatMoneyWhole(cents) {
      const amount = Math.round(cents / 100).toString()
      return `$${amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
    }

    updateMedia() {
      this.mediaItems.forEach((item) => {
        const duration = item.dataset.duration
        if (duration === this.currentDuration) {
          item.classList.add('active')
        } else {
          item.classList.remove('active')
        }
      })
    }

    enableForm() {
      if (this.addToCartButton) {
        this.addToCartButton.disabled = false
      }
      if (this.addToCartText) {
        this.addToCartText.textContent = 'Subscribe'
      }
    }

    disableForm(message = 'Unavailable') {
      if (this.addToCartButton) {
        this.addToCartButton.disabled = true
      }
      if (this.addToCartText) {
        this.addToCartText.textContent = message
      }
    }

    // --- Flavor Exclusion Modal Methods ---

    isExclusionEligible() {
      return (
        EXCLUSION_ELIGIBLE_TRACKS.includes(this.currentProductKey) &&
        this.currentDuration === EXCLUSION_ELIGIBLE_DURATION
      )
    }

    handleFormSubmit(e) {
      if (this._submittingFromModal) {
        this._submittingFromModal = false
        return // allow the submit to proceed
      }

      if (this.isExclusionEligible()) {
        e.preventDefault()
        this.showExclusionModal()
      } else if (this.excludedFlavorsInput) {
        // Non-eligible config — ensure no exclusion property is submitted
        this.excludedFlavorsInput.removeAttribute('name')
        this.excludedFlavorsInput.value = ''
      }
    }

    showExclusionModal() {
      if (!this.exclusionModal) return
      const isMashup = this.currentProductKey === 'mashup'
      // Show sweet exclusion section only for mashup
      if (this.sweetExclusionSection) {
        this.sweetExclusionSection.hidden = !isMashup
      }
      // Show savory subtitle only for mashup (when both sections visible)
      if (this.savoryExclusionSubtitle) {
        this.savoryExclusionSubtitle.hidden = !isMashup
      }
      // Restore checkbox state from current excludedFlavors
      this.exclusionCheckboxes.forEach((cb) => {
        cb.checked = this.excludedFlavors.includes(cb.value)
      })
      this.updateExclusionSummary()
      this.exclusionModal.hidden = false
      document.body.classList.add('overflow-hidden')
      // Focus first visible checkbox for accessibility
      const firstCheckbox = this.exclusionModal.querySelector('[data-flavor-exclude]')
      if (firstCheckbox) firstCheckbox.focus()
    }

    hideExclusionModal() {
      if (!this.exclusionModal) return
      this.exclusionModal.hidden = true
      document.body.classList.remove('overflow-hidden')
      if (this.addToCartButton) this.addToCartButton.focus()
    }

    updateExclusionSummary() {
      if (!this.exclusionSummary || !this.exclusionSummaryText) return
      const checked = Array.from(this.exclusionCheckboxes).filter((cb) => cb.checked)
      if (checked.length > 0) {
        this.exclusionSummary.hidden = false
        this.exclusionSummaryText.textContent = checked.map((cb) => cb.value).join(', ')
      } else {
        this.exclusionSummary.hidden = true
        this.exclusionSummaryText.textContent = ''
      }
    }

    confirmExclusions() {
      this.excludedFlavors = Array.from(this.exclusionCheckboxes)
        .filter((cb) => cb.checked)
        .map((cb) => cb.value)

      if (this.excludedFlavorsInput) {
        if (this.excludedFlavors.length > 0) {
          this.excludedFlavorsInput.name = 'properties[Excluded Flavors]'
          this.excludedFlavorsInput.value = this.excludedFlavors.join(', ')
        } else {
          // No exclusions selected — don't submit the property
          this.excludedFlavorsInput.removeAttribute('name')
          this.excludedFlavorsInput.value = ''
        }
      }

      this.hideExclusionModal()
      this._submittingFromModal = true
      this.form.submit()
    }

    skipExclusions() {
      this.excludedFlavors = []
      if (this.excludedFlavorsInput) {
        this.excludedFlavorsInput.removeAttribute('name')
        this.excludedFlavorsInput.value = ''
      }

      this.hideExclusionModal()
      this._submittingFromModal = true
      this.form.submit()
    }

    clearExclusions() {
      this.excludedFlavors = []
      if (this.excludedFlavorsInput) {
        this.excludedFlavorsInput.removeAttribute('name')
        this.excludedFlavorsInput.value = ''
      }
      if (this.exclusionCheckboxes) {
        this.exclusionCheckboxes.forEach((cb) => {
          cb.checked = false
        })
      }
    }
  }

  if (!customElements.get(SELECTOR)) {
    customElements.define(SELECTOR, SubscriptionSelector)
  }
})()
